import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isAuthenticated, updateUserProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid verification link. No token provided.');
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        await api.get(`/auth/verify-email?token=${token}`);
        setSuccess(true);

        // Update Redux state so banner disappears without page refresh
        if (isAuthenticated) {
          updateUserProfile({ email_verified: true });
        }
      } catch (err) {
        // A link that was already used is usually not a failure (20 Sep 2026
        // audit): mail scanners such as Outlook Safe Links open every link
        // before the person does, which spends the token — they then click and
        // were told "Invalid verification token" although they ARE verified.
        // If we can see that the signed-in account is verified, say so.
        let alreadyVerified = false;
        if (isAuthenticated) {
          try {
            const me = await api.get('/users/profile');
            const u = me?.data?.data?.user || me?.data?.data || me?.data?.user;
            alreadyVerified = !!(u?.emailVerified ?? u?.email_verified);
          } catch (_) { /* fall through to the plain message */ }
        }
        if (alreadyVerified) {
          setSuccess(true);
          updateUserProfile({ email_verified: true });
        } else {
          setError(
            'This link has already been used or has expired. If you have already verified your email you are all set — '
            + 'otherwise sign in and press "Resend email" on the banner for a fresh link.'
          );
        }
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-page)'
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom textAlign="center">
            Kodahfy
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            Email Verification
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Verifying your email address...
              </Typography>
            </Box>
          ) : success ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your email has been verified successfully!
              </Alert>
              <Typography variant="body2" textAlign="center" sx={{ mt: 2 }}>
                <Link component={RouterLink} to={isAuthenticated ? '/dashboard' : '/login'}>
                  {isAuthenticated ? 'Go to Dashboard' : 'Sign In'}
                </Link>
              </Typography>
            </>
          ) : (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Typography variant="body2" textAlign="center" sx={{ mt: 2 }}>
                <Link component={RouterLink} to={isAuthenticated ? '/dashboard' : '/login'}>
                  {isAuthenticated ? 'Go to Dashboard' : 'Sign In'}
                </Link>
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyEmail;
